import { isNil } from 'lodash';
import moment from 'moment-timezone';

import { ACTION_TYPE_DESCRIPTOR } from '~/components/AiChat/SideBarDialog/Actions/Action/ActionTypes';
import { type AiAction, isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import type { EntryKind, StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import {
  ClaimConductorAsksResponseAgentClosedAs,
  ClaimConductorAsksResponseAskStatus,
  DynamicClaimActionResultRequiresHumanApprovalReasonKey as WaitingReasonKey,
} from '~/server_shared/openapi/data-contracts';

import { ACTION_STATE_LABEL, KIND_APPEARANCE } from './constants';
import { TEXTS } from './texts';
import type {
  ActionState,
  ActivityKind,
  AskState,
  ConductorAsk,
  ConductorQuestion,
  ConductorRequest,
  NotifiedActionState,
  WaitingReason,
} from './types';

/**
 * The action this entry mentions, whatever kind of entry it is.
 *
 * `metadata` is a Firestore document, so every value in it is `unknown` and the `typeof` test is
 * the only thing that makes this a `string`.
 */
export const getReferencedActionId = (notification: StpNotification): string | null => {
  const actionId = notification.metadata?.action_id;

  return typeof actionId === 'string' ? actionId : null;
};

/**
 * Whether this entry is about an action. Decided by `entry_kind`, not by `action_id`: a handler
 * turn carries the id of the card it decided, and an action entry can carry no id at all.
 */
export const isActionEntry = (notification: StpNotification): boolean => notification.metadata?.entry_kind === 'action';

/** The row an action entry joins to, where it names one; `null` for any other entry. */
export const getNotifiedActionId = (notification: StpNotification): string | null =>
  isActionEntry(notification) ? getReferencedActionId(notification) : null;

/** Whether this entry is a handler's turn: the note they wrote when rejecting a card. */
export const isHandlerTurn = (notification: StpNotification): boolean =>
  notification.metadata?.entry_kind === 'handler_turn';

/**
 * Whether an entry of this kind is about an ask. claims-ai narrates an ask, raised or closed, under
 * the ask's own kind, so the entry kind is the ask's kind too.
 */
export const isAskEntryKind = (entryKind?: EntryKind): entryKind is ConductorAsk['kind'] =>
  entryKind === 'question' || entryKind === 'request';

/** The ask an ask entry is about; `null` for any other entry. */
export const getNotifiedAskId = (notification: StpNotification): string | null =>
  isAskEntryKind(notification.metadata?.entry_kind) ? (notification.metadata?.ask_id ?? null) : null;

/**
 * The entry the log draws each ask at, by ask: the first entry about it, in the order given — the
 * raise, unless the window starts after it. claims-ai narrates an ask's closure under the ask's own
 * kind, so a later entry about the same ask belongs to the card the first one drew.
 */
export const getDrawnAskEntries = (notifications: StpNotification[]): Map<string, StpNotification> => {
  const drawn = new Map<string, StpNotification>();

  notifications.forEach((notification) => {
    const askId = getNotifiedAskId(notification);
    if (askId && !drawn.has(askId)) drawn.set(askId, notification);
  });

  return drawn;
};

/**
 * The ask's state now, read off the ask and never off an entry.
 *
 * An entry's `is_closed` cannot say how an ask closed, and a handler answering or rejecting one
 * publishes no entry at all. The ask says both: `agent_closure` where Clive closed it,
 * `human_response` where the handler did, and neither while it is open. The state is read off the
 * closure itself rather than off the ask's kind: the contract lets either gesture and either
 * closure land on either kind, so the kind cannot say which one happened.
 */
export const resolveAskState = (ask: ConductorAsk): AskState => {
  if (ask.agent_closure) {
    return ask.agent_closure.closed_as === ClaimConductorAsksResponseAgentClosedAs.Resolved ? 'done' : 'withdrawn';
  }

  const gesture = ask.human_response?.gesture;
  switch (gesture) {
    case 'answered':
      return 'answered';
    case 'rejected':
      return 'rejected';
    case undefined:
      // `status` says whether the ask is still the handler's: one closed with no closure on record
      // is no longer waiting on anybody, whoever closed it.
      return ask.status === ClaimConductorAsksResponseAskStatus.Closed ? 'withdrawn' : 'pending';
    default: {
      // A gesture the contract does not have yet must be given a state before this compiles.
      const unhandled: never = gesture;
      return unhandled;
    }
  }
};

/** The state the notification declared, where it declared one the conductor actually publishes. */
export const getNotifiedActionState = (value: unknown): NotifiedActionState | undefined =>
  value === 'executed' || value === 'awaiting_approval' || value === 'failed' ? value : undefined;

const fromNotifiedState = (notifiedState?: NotifiedActionState): ActionState => {
  if (notifiedState === 'executed') return 'executed';
  if (notifiedState === 'failed') return 'failed';
  if (notifiedState === 'awaiting_approval') return 'pending';

  // Neither source said anything — which is the live state for as long as the actions request is
  // in flight. `pending` here would put "Awaiting approval" on a card nothing has established is
  // waiting for anyone.
  return 'unresolved';
};

/**
 * The action's state now.
 *
 * Two sources, and the action row wins: `action_state` on the notification is a snapshot of the
 * moment the conductor published it and never moves again, while the row carries what has since
 * become of the card — a handler executing or rejecting it publishes no notification at all. The
 * notification is read only for `failed`, which the row cannot express (a failed execution leaves
 * it neither executed nor ignored), and as the whole answer when the row is not to hand.
 */
export const resolveActionState = (action?: AiAction, notifiedState?: NotifiedActionState): ActionState => {
  if (!action) return fromNotifiedState(notifiedState);
  if (action.is_executed) return 'executed';
  if (action.is_ignored) {
    const actor = action.status_change?.actor?.type;
    if (actor === 'agent') return 'withdrawn';
    if (actor === 'user') return 'rejected';

    return 'dismissed';
  }
  if (notifiedState === 'failed') return 'failed';

  // The row is to hand and says the action is neither executed nor ignored, so it really is
  // waiting on somebody — unlike the no-row case above, this one is established.
  return 'pending';
};

/**
 * The state, naming the person where a person did it.
 *
 * A claim file is read by the handler's colleagues, their team lead and an auditor, so the record
 * names the party rather than saying "you". Only the two human transitions take a name; the rest
 * are Clive's or nobody's and already say so.
 */
export const actionStateLabel = (state: ActionState, action?: AiAction): string => {
  const actor = action?.status_change?.actor;
  if (actor?.type !== 'user') return ACTION_STATE_LABEL[state];

  // A person did this, whether or not the AI service kept their name (`display_name` is optional
  // on the actor). Falling back to the bare label is only safe where that label claims no actor:
  // "Rejected" does, "Executed automatically" does not — it would credit Clive with a person's
  // decision.
  const who = actor.display_name;
  if (!who) return state === 'executed' ? 'Executed' : ACTION_STATE_LABEL[state];
  if (state === 'executed') return `Executed by ${who}`;
  if (state === 'rejected') return `Rejected by ${who}`;

  return ACTION_STATE_LABEL[state];
};

/**
 * An ask's state chip, naming the person where a person closed it.
 *
 * Only the state still outstanding says "you": a closed ask is part of the record, which names the
 * party rather than whoever is reading it. The contract lets either gesture and either closure land
 * on either kind, so every state has words for both; only Clive's resolution reads differently,
 * because a question asks for no work that could arrive.
 */
export const askStateLabel = (state: AskState, ask: ConductorAsk): string => {
  const response = ask.human_response;
  switch (state) {
    case 'pending':
      return 'Waiting on you';
    case 'done':
      return ask.kind === 'request' ? 'Done — closed by Clive' : 'Closed by Clive';
    case 'withdrawn':
      return 'No longer relevant';
    // `resolveAskState` reaches these two only off the response itself, so it is on record here;
    // the bare label is what the types need for the case it is not, and it claims no actor.
    case 'rejected':
      return response?.gesture === 'rejected' ? `Rejected by ${response.actor.display_name}` : 'Rejected';
    case 'answered':
      return response?.gesture === 'answered' ? `Answered by ${response.actor.display_name}` : 'Answered';
    default: {
      // A state the asks do not have yet must be given a label before this compiles.
      const unhandled: never = state;
      return unhandled;
    }
  }
};

/** A request's state chip in the log. None while it is pending: the purple card already says so. */
export const requestStateLabel = (state: AskState, request: ConductorRequest): string | null =>
  state === 'pending' ? null : askStateLabel(state, request);

/** The line a withdrawn ask's card ends on, worded the same for a request and a question. */
const WITHDRAWN_OUTCOME = 'Clive withdrew this — the claim moved on.';

/**
 * What became of a closed request, in the one line its card then shows; `null` while it is open.
 */
export const requestOutcome = (state: AskState, request: ConductorRequest): string | null => {
  const response = request.human_response;
  switch (state) {
    case 'pending':
      return null;
    case 'done':
      return 'Clive closed this — the work arrived.';
    case 'withdrawn':
      return WITHDRAWN_OUTCOME;
    // The chip names who rejected it, and the note is under a label of its own.
    case 'rejected':
      return null;
    // As in `askStateLabel`, the response is on record whenever this is reached. Without it there is
    // nobody to name and nothing to quote, and a sentence with neither says nothing.
    case 'answered':
      return response?.gesture === 'answered'
        ? `${response.actor.display_name} answered this — “${response.answer}”.`
        : null;
    default: {
      // A state the asks do not have yet must be given an outcome before this compiles.
      const unhandled: never = state;
      return unhandled;
    }
  }
};

/**
 * What became of a closed question, in the line under its record; `null` while it is open.
 *
 * The log's question card wears no state chip, so the line names who closed it and quotes nothing:
 * the record already shows the answer, and a rejection's note is under a label of its own. claims-ai
 * only ever answers or withdraws a question, but the contract lets the other two closures land on
 * one, so they have words too.
 */
export const questionOutcome = (state: AskState, question: ConductorQuestion): string | null => {
  switch (state) {
    case 'pending':
      return null;
    // The words the chip would say, which this card does not wear.
    case 'answered':
    case 'rejected':
      return askStateLabel(state, question);
    case 'withdrawn':
      return WITHDRAWN_OUTCOME;
    case 'done':
      return 'Clive closed this.';
    default: {
      // A state the asks do not have yet must be given an outcome before this compiles.
      const unhandled: never = state;
      return unhandled;
    }
  }
};

/** The note a person rejected an ask with, which its card shows under a label of its own. */
export const askRejectionReason = (state: AskState, ask: ConductorAsk): string | null => {
  const response = ask.human_response;

  return state === 'rejected' && response?.gesture === 'rejected' ? response.reason : null;
};

/**
 * The note an action was closed with without being executed — rejected, withdrawn or dismissed —
 * which its card shows under the label `actionReasonLabel` gives it. A note on anything but a
 * dismissal is never shown.
 */
export const actionRejectionReason = (state: ActionState, action: AiAction): string | null => {
  const isClosedUnexecuted = state === 'rejected' || state === 'withdrawn' || state === 'dismissed';

  return isClosedUnexecuted ? (action.status_change?.reason ?? null) : null;
};

/** The label over that note: Clive withdrawing an action is not a rejection, so its note is not called one. */
export const actionReasonLabel = (state: ActionState): string =>
  state === 'withdrawn' ? TEXTS.REASON : TEXTS.REJECTION_REASON;

const WAITING_REASON_KEYS: ReadonlySet<string> = new Set(Object.values(WaitingReasonKey));

/** Whether this build has a chip for the cause: the AI service passes on whatever key CMS sent. */
const isWaitingReasonKey = (key: string): key is WaitingReasonKey => WAITING_REASON_KEYS.has(key);

/**
 * Why a waiting action is waiting, where CMS said so and this build knows the cause. CMS gives the
 * reason when it refuses to run the action unattended, and the AI service keeps it on the action.
 *
 * Only a `pending` action has one: once the action is executed, closed or has failed, why it was held
 * no longer says anything about where it is. An action saved before the reason was kept, or with a
 * cause this build does not know, has none either, and keeps the generic `pending` chip.
 */
export const actionWaitingReason = (state: ActionState, action?: AiAction): WaitingReason | null => {
  const taskData = action?.task_data;
  if (state !== 'pending' || !isDynamicClaimActionTaskData(taskData)) return null;

  const reason = taskData.requires_human_approval_reason;
  if (isNil(reason) || !isWaitingReasonKey(reason.key)) return null;

  return { key: reason.key, text: reason.text };
};

/**
 * The handler's own words, where they answered the question in them. Only an answer given as one of
 * the options is not: the same words typed freely are the handler's own.
 */
export const ownWordsAnswer = (question: ConductorQuestion): string | null => {
  const response = question.human_response;
  if (response?.gesture !== 'answered') return null;

  return response.from_options && question.options.includes(response.answer) ? null : response.answer;
};

/**
 * The thing itself, in words — never the kind, which the icon says.
 *
 * Both panes and the handler turn read it, and the title comes from the same descriptor the Clive
 * Insights card reads, so one recommendation is called one thing wherever a handler meets it. An
 * action type the descriptor does not know falls back to the text the model wrote, which is what
 * the dynamic action's own descriptor does with it: a card with no title is unreadable, and the
 * type is the least of what it has to say.
 *
 * The last fallback is the kind's own label rather than `''`. `task_text` is a required string on
 * the wire and so survives `??`, but nothing forbids it being empty, and an unknown type carrying
 * an empty one would otherwise produce exactly the titleless card this chain exists to avoid.
 */
export const actionTitle = (action: AiAction): string => {
  const { title } = ACTION_TYPE_DESCRIPTOR[action.task_data?.task_type ?? ''] ?? {};
  if (typeof title === 'function') return title(action);

  return title || action.task_data?.task_text || KIND_APPEARANCE.recommendation.label;
};

/**
 * When an entry or a card was raised: the local date and time down to the second, in the
 * organization's locale.
 *
 * Entries span days and can land within the same minute, so neither the time alone nor HH:MM tells
 * them apart. Locale rather than a fixed pattern, so an organization on a day-first or 12h locale
 * keeps reading its own order. A timestamp without an offset is read as UTC, as
 * `serverDateTimeToLocal` reads the server's times. Returns '' for an unparseable timestamp instead
 * of throwing: these render per row beside live data, and one bad row must not take the list with it.
 */
/** moment's locale-aware date (`L`) and time with seconds (`LTS`). */
const ENTRY_DATE_TIME_FORMAT = 'L LTS';

const formatEntryMoment = (dateTime: string | Date | undefined, format: string): string => {
  if (!dateTime) {
    return '';
  }

  const parsed = moment.utc(dateTime, moment.ISO_8601);

  return parsed.isValid() ? parsed.local().format(format) : '';
};

export const formatEntryDateTime = (dateTime?: string | Date): string =>
  formatEntryMoment(dateTime, ENTRY_DATE_TIME_FORMAT);

/**
 * The local time to the minute, in the organization's locale (`LT`), read as `formatEntryDateTime` reads
 * a timestamp and with its '' for an unparseable one. For the conversation with Clive, where a time names
 * one of a handful of messages — "sent 14:02" — rather than ordering the log.
 */
export const formatEntryTime = (dateTime?: string | Date): string => formatEntryMoment(dateTime, 'LT');

/** The key of an activity pane row, unique across kinds. */
export const activityRowKey = (kind: ActivityKind, id: string): string => `${kind}:${id}`;

/** The DOM id of a log entry, so another pane can bring it into view. */
export const logEntryElementId = (eventId: string): string => `conductor-log-entry-${eventId}`;

/** The DOM id of an activity pane card, by its row's key, so the log can say which card it shows. */
export const activityCardElementId = (key: string): string => `conductor-activity-card-${key}`;

/** Whether the reader asked their system for less motion, which the log's animations then skip. */
export const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/** Flashes a log entry's box, so the reader sees which entry they were taken to, or which one just came in. */
export const flashLogEntry = (element: HTMLElement): void => {
  // A one-shot flash through the Web Animations API, so no highlight state; jsdom has no `animate`.
  element.animate?.([{ backgroundColor: 'rgb(219 234 254)' }, { backgroundColor: 'transparent' }], { duration: 1500 });
};

/** Whether the log draws a box of its own for an event: not for one it folds into another entry's card, or filters out. */
export const isLogEntryDrawn = (eventId: string): boolean =>
  document.getElementById(logEntryElementId(eventId)) !== null;

/**
 * Scrolls the log to an entry, to the middle of the view or where `block` says, and flashes it, so the reader sees which
 * one they were taken to.
 */
export const scrollToLogEntry = (eventId: string, block: ScrollLogicalPosition = 'center'): void => {
  const element = document.getElementById(logEntryElementId(eventId));
  if (!element) return;

  // Instant, so the log reads the reader as away from its newest end before it next follows it; the flash shows where
  // they landed.
  element.scrollIntoView({ behavior: 'instant', block });
  flashLogEntry(element);
};
